import React from "react";
import { ChevronDown } from "lucide-react";
import { TemplateModule } from "../../../../views/marketing-pages/templates";
import { ActionNode } from "../../../../views/convoauto-pages/bot-studio/types";
import { CardShell, FieldLabel } from "./card-handler";

function extractVariableSlots(content: string): number[] {
  const found = new Set<number>();
  for (const m of content.matchAll(/\{\{(\d+)\}\}/g)) {
    found.add(Number(m[1]));  // Use .add() instead of .set()
  }
  return Array.from(found).sort((a, b) => a - b);
}

function renderPreview(content: string, vars: Record<number, string>): string {
  return content.replace(/\{\{(\d+)\}\}/g, (_, n) => {
    const v = vars[Number(n)];
    return v?.trim() ? v : `{{${n}}}`;
  });
}

function parseButtons(tpl: TemplateModule): string[] {
  if (!tpl.buttons || !Array.isArray(tpl.buttons)) return [];
  return tpl.buttons
    .map((b: any) => (typeof b === "string" ? b : b?.text ?? ""))
    .filter(Boolean);
}

interface SendMessageTemplateActionCardProps {
  node: ActionNode;
  templates?: TemplateModule[];
  onUpdate: (config: Record<string, any>) => void;
  onDelete: () => void;
  onReset: () => void;
  bare?: boolean;
}

export const SendMessageTemplateActionCard: React.FC<SendMessageTemplateActionCardProps> = ({
  node,
  templates = [],
  onUpdate,
  onDelete,
  onReset,
  bare,
}) => {
  const config = node.config ?? {};

      const whatsappTemplates = templates.filter((t) => t.channel === "WhatsApp");
      const selectedTemplate: TemplateModule | null =
        whatsappTemplates.find((t) => t.id === config.templateId) ?? null;
    

  const variableSlots = selectedTemplate
    ? extractVariableSlots(selectedTemplate.content ?? "")
    : [];

  const variables: Record<number, string> = config.variables ?? {};
  const previewBody = selectedTemplate
    ? renderPreview(selectedTemplate.content ?? "", variables)
    : "";
  const buttons = selectedTemplate ? parseButtons(selectedTemplate) : [];

  const setVariable = (index: number, value: string) =>
    onUpdate({ ...config, variables: { ...variables, [index]: value } });

  const handleTemplateChange = (templateId: string) => {
    const tpl = templates.find((t) => t.id === templateId) ?? null;
    // If template has a media header, propagate to media_url / media_type so
    // celery send_whatsapp attaches the required header component for Meta.
    const headerType = (tpl?.header_type ?? "").toLowerCase().trim();
    const isMediaHeader = ["image", "video", "document", "audio"].includes(headerType);

    onUpdate({
      ...config,
      templateId: tpl?.id ?? "",
      templateName: tpl?.template_name ?? "",
      language: tpl?.language ?? "",
      channel: tpl?.channel ?? "",
      waba_id: tpl?.waba_id ?? "",
      category: tpl?.category ?? "",
      wa_template_id: tpl?.wa_template_id ?? "",
      variables: {},
      media_url: isMediaHeader ? (tpl?.header_content ?? "") : null,
      media_type: isMediaHeader ? headerType : null,
    });
  };

  return (
    <CardShell
      title="Send Message Template"
      description="Select and send a message template to customers"
      onDelete={onDelete}
      onReset={onReset}
      bare={bare}
    >
        <div className="relative mb-3">
          <FieldLabel>Select Template</FieldLabel>
          <div className="relative">
            <select
              value={config.templateId ?? ""}
              onChange={(e) => handleTemplateChange(e.target.value)}
              className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm appearance-none bg-white dark:bg-gray-900 pr-8 focus:outline-none focus:ring-1 focus:ring-indigo-300"
            >
              <option value=""> choose template </option>
              {whatsappTemplates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.template_name}
                </option>
              ))}
            </select>
            <ChevronDown size={16} className="absolute right-2 top-2.5 text-[#7B7B7B] font-semibold dark:text-gray-500 pointer-events-none" />
          </div>
        </div>

        {selectedTemplate && (
          <>
            <div className="mb-3">
              <FieldLabel>Channel</FieldLabel>
              <div className="flex items-center gap-2 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 bg-gray-50 dark:bg-gray-800">
                {selectedTemplate.channel?.toLowerCase() === "whatsapp" && (
                  <svg viewBox="0 0 24 24" className="w-4 h-4 flex-shrink-0" fill="#25D366">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                    <path d="M12 0C5.373 0 0 5.373 0 12c0 2.124.558 4.118 1.532 5.845L.057 23.273a.75.75 0 0 0 .924.924l5.386-1.479A11.943 11.943 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.75a9.697 9.697 0 0 1-4.948-1.355l-.355-.212-3.666 1.007 1.003-3.681-.23-.368A9.698 9.698 0 0 1 2.25 12C2.25 6.615 6.615 2.25 12 2.25S21.75 6.615 21.75 12 17.385 21.75 12 21.75z" />
                  </svg>
                )}
                <span className="text-sm text-gray-700 dark:text-gray-300">{selectedTemplate.channel}</span>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden mb-3">
              {selectedTemplate.header_type === "image" && selectedTemplate.header_content && (
                <div className="w-full h-28 bg-gray-100 dark:bg-gray-800 overflow-hidden">
                  <img
                    src={selectedTemplate.header_content}
                    alt="Template header"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const parent = e.currentTarget.parentElement;
                      if (parent) parent.style.display = "none";
                    }}
                  />
                </div>
              )}

              <div className="px-3 py-2.5 text-xs text-gray-600 dark:text-gray-300 whitespace-pre-line leading-relaxed">
                {previewBody}
              </div>

              {selectedTemplate.footer && (
                <div className="px-3 pb-2 text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 italic border-t border-gray-100 dark:border-gray-800 pt-1.5">
                  {selectedTemplate.footer}
                </div>
              )}

              {buttons.length > 0 && (
                <>
                  <div className="border-t border-gray-100 dark:border-gray-800">
                    {buttons.map((btn, i) => (
                      <div key={i} className="px-3 py-1.5 text-xs text-indigo-600 text-center border-b border-gray-100 dark:border-gray-800 last:border-b-0">
                        {btn}
                      </div>
                    ))}
                  </div>
                  <div className="px-3 py-2 space-y-1.5 border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800">
                    {buttons.map((btn, i) => (
                      <div key={i} className="flex items-center justify-between bg-white dark:bg-gray-900 rounded-lg px-2 py-1.5 border border-gray-100 dark:border-gray-800">
                        <span className="text-xs text-gray-700 dark:text-gray-300">{btn}</span>
                        <div className="w-3 h-3 rounded-full bg-indigo-500 border-2 border-white shadow" />
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {variableSlots.length > 0 && (
              <div className="mb-3">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Template Variables</p>
                <div className="space-y-2">
                  {variableSlots.map((idx) => {
                    const varDef = selectedTemplate.variables?.find((v) => v.index === idx);
                    return (
                      <div key={idx}>
                        <label className="block text-xs mb-0.5">
                          <span className="font-medium text-gray-600 dark:text-gray-300">{`{{${idx}}}`}</span>
                          {varDef?.name && <span className="ml-1 text-[#7B7B7B] font-semibold dark:text-gray-500">— {varDef.name}</span>}
                        </label>
                        <input
                          className="w-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-transparent text-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm outline-none focus:ring-1 focus:ring-indigo-300 placeholder-gray-300 dark:placeholder-gray-500"
                          placeholder={varDef?.example ?? `Value for {{${idx}}}`}
                          value={variables[idx] ?? ""}
                          onChange={(e) => setVariable(idx, e.target.value)}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div>
              <FieldLabel>Language</FieldLabel>
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800">
                {selectedTemplate.language}
              </div>
            </div>
          </>
        )}
    </CardShell>
  );
};